import {
    Injectable,
    Optional
} from '@angular/core';
import {
    from,
    Observable,
    of,
    Subscriber
} from 'rxjs';
import { DatDbConfig } from '../../models';
import PouchDB from 'pouchdb';
import {
    IDatDbClientDb,
    IDatDbClientService
} from '../models';
import {
    DatDbClientBatch,
    IDatDbResponse,
    IDatDbResponseBatch,
} from './models';
import { noop } from 'lodash';
import { map } from 'rxjs/operators';
import { DatDbClientMapperService } from './dat-db-client-mapper.service';

declare const global;

@Injectable({
    providedIn: 'root',
})
export class DatDbClientService implements IDatDbClientService {
    private DB_NAMES: string[];
    private DEFAULT_DB: string;
    private _dbs: IDatDbClientDb = {};
    private _initialized: boolean = false;

    constructor(
        private _mapper: DatDbClientMapperService,
        @Optional() config: DatDbConfig
    ) {
        this.DB_NAMES = !!config?.dbs
            ? config?.dbs
            : !!config?.db
            ? [config?.db]
            : new DatDbConfig().dbs;
        this.DEFAULT_DB = !!config?.defaultDb
            ? config?.defaultDb
            : !!config?.db
            ? config?.db
            : new DatDbConfig().defaultDb;
    }

    isInitialized(): boolean {
        return this._initialized;
    }

    initialize(): Promise<boolean> {
        !this._initialized ? this.createDBs(this.DB_NAMES) : noop();
        this._initialized = true;

        return new Promise((res) => res(true));
    }

    createDBs(
        dbs: string[],
        options: PouchDB.Configuration.DatabaseConfiguration = {},
    ): IDatDbClientDb {
        const dbClient: IDatDbClientDb = dbs.reduce(
            (currentDbs: IDatDbClientDb, dbName: string): IDatDbClientDb => ({
                ...currentDbs,
                [dbName]: new PouchDB(dbName, options),
            }),
            { ...this._dbs } as IDatDbClientDb,
        );

        this._dbs = dbClient;

        return dbClient;
    }

    getDoc(docName: string, opts?: { dbName: string }): Observable<unknown> {
        const db: PouchDB.Database = this.getDb(opts);

        // NOTE: using any to avoid conflict with PouchDB expected types
        return from(
            db?.get(docName).then((response: any) => response.doc as unknown)
        );
    }

    hasDoc(docName: string, opts?: { dbName: string }): Observable<boolean> {
        const db: PouchDB.Database = this.getDb(opts);

        return !!db
            ? (from(
                  db
                      .get(docName)
                      .catch((err) => (err.name === 'not_found' ? false : null))
                      .then((isFound: boolean) => {
                          return !!isFound;
                      })
              ) as Observable<boolean>)
            : of(false);
    }

    setDoc(
        docName: string,
        doc: unknown,
        opts?: { dbName: string }
    ): Observable<unknown> {
        const db: PouchDB.Database = this.getDb(opts);

        return from(
            db
                .get(docName)
                .catch((err) => (err.name === 'not_found' ? {} : null))
                .then((response: IDatDbResponse) => {
                    return db.put({
                        ...response,
                        _id: docName,
                        doc: doc,
                    }).catch(
                        (err) => {
                            if (err.name === 'conflict') {
                                noop();
                            }
                        }
                    );
                })
        );
    }

    getDb(opts: { dbName: string }): PouchDB.Database {
        const dbName: string = !!opts?.dbName ? opts.dbName : this.DEFAULT_DB;

        return !!this._dbs[dbName]
            ? this._dbs[dbName]
            : this._dbs[this.DEFAULT_DB];
    }

    getAllDocs(opts: { dbName: string }): Observable<DatDbClientBatch> {
        return from(this.getDb(opts).allDocs({ include_docs: true })).pipe(
            map(
                (docs: unknown): DatDbClientBatch =>
                    this._mapper.mapDatDbBatch(docs as IDatDbResponseBatch)
            )
        );
    }

    deleteDb(opts: { dbName: string }): Observable<boolean> {
        return new Observable<boolean>((subscribe: Subscriber<boolean>) => {
            const db = this.getDb(opts);
            db.destroy(undefined, (err) => subscribe.next(!err));
            return subscribe;
        });
    }
}
